import { useEffect, useRef, useState } from "react";
import {
  capturePaypalOrder,
  createPaypalOrder,
  getPaypalConfig,
} from "../../services/paypal";

let sdkPromise: Promise<any> | null = null;

const loadPaypalSdk = async () => {
  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = (async () => {
    const config = await getPaypalConfig();

    if (!config?.clientId) {
      throw new Error("PayPal is not configured");
    }

    if ((window as any).paypal) {
      return (window as any).paypal;
    }

    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      // the client id decides by itself whether this is sandbox or live
      script.src = `https://www.paypal.com/sdk/js?client-id=${config.clientId}&currency=${
        config.currency || "USD"
      }&intent=capture&components=buttons`;
      script.onload = resolve;
      script.onerror = () => reject(new Error("PayPal SDK failed to load"));
      document.body.appendChild(script);
    });

    return (window as any).paypal;
  })();

  // let a later attempt retry instead of caching the failure forever
  sdkPromise.catch(() => {
    sdkPromise = null;
  });

  return sdkPromise;
};

interface PaypalButtonProps {
  getPayload: () => any | null;
  onSuccess: (invoiceId: string) => void;
  onFailure: (reason?: string) => void;
}

export const PaypalButton = ({
  getPayload,
  onSuccess,
  onFailure,
}: PaypalButtonProps) => {
  const container = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // the buttons are rendered once by the SDK, so the callbacks are read from
  // refs to always see the current form state without re-rendering them
  const handlers = useRef({ getPayload, onSuccess, onFailure });
  handlers.current = { getPayload, onSuccess, onFailure };

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      try {
        const paypal = await loadPaypalSdk();

        if (cancelled || !container.current) {
          return;
        }

        container.current.innerHTML = "";

        await paypal
          .Buttons({
            // only the PayPal button, no card / SEPA / local methods
            fundingSource: paypal.FUNDING.PAYPAL,
            style: { layout: "vertical", color: "gold", shape: "rect" },
            createOrder: async () => {
              const payload = handlers.current.getPayload();

              if (!payload) {
                throw new Error("invalidForm");
              }

              const data = await createPaypalOrder(payload);

              if (data?.error || !data?.invoiceId) {
                handlers.current.onFailure(data?.message);
                throw new Error(data?.message || "createOrderFailed");
              }

              return data.invoiceId;
            },
            onApprove: async (data: any) => {
              const result = await capturePaypalOrder(data.orderID);

              if (result?.status === "success") {
                handlers.current.onSuccess(data.orderID);
              } else {
                handlers.current.onFailure(result?.status);
              }
            },
            onCancel: () => {
              handlers.current.onFailure("cancelled");
            },
            onError: () => {
              handlers.current.onFailure();
            },
          })
          .render(container.current);

        if (!cancelled) {
          setLoading(false);
        }
      } catch (error) {
        console.log(error);
        if (!cancelled) {
          setLoading(false);
          setFailed(true);
        }
      }
    };

    render();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="order-placement__paypal">
      {loading && (
        <p className="order-placement__subtitle" style={{ display: "block" }}>
          PayPal...
        </p>
      )}
      {failed && (
        <p className="order-placement__subtitle" style={{ display: "block" }}>
          PayPal is unavailable right now
        </p>
      )}
      <div ref={container}></div>
    </div>
  );
};
